import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { countriesAPI, adminAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ArrowRight, Upload, X } from 'lucide-react';

const AdminCountries = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    name_fa: '',
    name_en: '',
    capital_fa: '',
    flag_emoji: '',
    population: '',
    official_language: '',
    currency: '',
    climate: '',
    avg_living_cost: '',
    avg_rent: '',
    avg_salary: '',
    job_market: '',
    security_score: 0,
    quality_of_life_score: 0,
    education_score: 0,
    healthcare_score: 0,
    transportation: '',
    residence_conditions: '',
    citizenship_conditions: '',
    best_cities: '',
    best_student_cities: '',
    top_fields: '',
    in_demand_jobs: '',
    pros: '',
    cons: '',
    image_url: '',
    major_cities: []
  });

  const [cityInput, setCityInput] = useState({ name: '', image: '' });

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        await fetchCountries();
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
  }, [navigate]);

  const fetchCountries = async () => {
    try {
      const response = await countriesAPI.getAll();
      setCountries(response.data);
    } catch (error) {
      toast.error('خطا در دریافت کشورها');
    }
  };

  const handleImageUpload = async (e, field = 'image_url') => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم فایل نباید بیشتر از 5 مگابایت باشد');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      
      if (field === 'city_image') {
        setCityInput({ ...cityInput, image: imageUrl });
      } else {
        setFormData({ ...formData, [field]: imageUrl });
      }
      toast.success('تصویر با موفقیت آپلود شد');
    } catch (error) {
      toast.error('خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const addCity = () => {
    if (!cityInput.name.trim()) {
      toast.error('نام شهر را وارد کنید');
      return;
    }
    const newCity = { name: cityInput.name, image: cityInput.image || 'https://via.placeholder.com/800' };
    setFormData({ ...formData, major_cities: [...formData.major_cities, newCity] });
    setCityInput({ name: '', image: '' });
  };

  const removeCity = (index) => {
    const updatedCities = formData.major_cities.filter((_, i) => i !== index);
    setFormData({ ...formData, major_cities: updatedCities });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const countryData = {
        ...formData,
        best_cities: formData.best_cities.split(',').map(c => c.trim()).filter(c => c),
        best_student_cities: formData.best_student_cities.split(',').map(c => c.trim()).filter(c => c),
        top_fields: formData.top_fields.split(',').map(f => f.trim()).filter(f => f),
        in_demand_jobs: formData.in_demand_jobs.split(',').map(j => j.trim()).filter(j => j),
        pros: formData.pros.split(',').map(p => p.trim()).filter(p => p),
        cons: formData.cons.split(',').map(c => c.trim()).filter(c => c),
      };

      if (editingCountry) {
        await countriesAPI.update(formData.id, countryData);
        toast.success('کشور با موفقیت ویرایش شد');
      } else {
        countryData.id = Date.now().toString();
        await countriesAPI.create(countryData);
        toast.success('کشور با موفقیت ایجاد شد');
      }

      setShowModal(false);
      resetForm();
      fetchCountries();
    } catch (error) {
      toast.error('خطا در ذخیره کشور');
    }
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      ...country,
      best_cities: country.best_cities?.join(', ') || '',
      best_student_cities: country.best_student_cities?.join(', ') || '',
      top_fields: country.top_fields?.join(', ') || '',
      in_demand_jobs: country.in_demand_jobs?.join(', ') || '',
      pros: country.pros?.join(', ') || '',
      cons: country.cons?.join(', ') || '',
      major_cities: country.major_cities || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این کشور اطمینان دارید؟')) return;

    try {
      await countriesAPI.delete(id);
      toast.success('کشور با موفقیت حذف شد');
      fetchCountries();
    } catch (error) {
      toast.error('خطا در حذف کشور');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '', name_fa: '', name_en: '', capital_fa: '', flag_emoji: '', population: '',
      official_language: '', currency: '', climate: '', avg_living_cost: '', avg_rent: '',
      avg_salary: '', job_market: '', security_score: 0, quality_of_life_score: 0,
      education_score: 0, healthcare_score: 0, transportation: '', residence_conditions: '',
      citizenship_conditions: '', best_cities: '', best_student_cities: '', top_fields: '',
      in_demand_jobs: '', pros: '', cons: '', image_url: '', major_cities: []
    });
    setEditingCountry(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8" data-testid="admin-countries-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors">
              <ArrowRight size={24} />
            </Link>
            <h1 className="text-3xl font-light text-white">مدیریت کشورها</h1>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            کشور جدید
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {countries.map((country) => (
            <div key={country.id} className="glass-effect border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all">
              <div className="h-48 bg-cover bg-center" style={{ backgroundImage: `url(${country.image_url})` }}>
                <div className="h-full bg-black/40 flex items-center justify-center">
                  <span className="text-6xl">{country.flag_emoji}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-xl font-medium text-white mb-1">{country.name_fa}</h3>
                <p className="text-sm text-zinc-400 mb-3">{country.capital_fa}</p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => handleEdit(country)} className="p-2 text-[#D4AF37] hover:bg-white/5 rounded-lg transition-colors">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(country.id)} className="p-2 text-red-400 hover:bg-white/5 rounded-lg transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
            <div className="glass-effect border border-white/10 rounded-2xl p-8 max-w-5xl w-full my-8" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-light text-white mb-6">{editingCountry ? 'ویرایش کشور' : 'کشور جدید'}</h2>

              <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto px-2">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">نام فارسی *</label>
                    <input type="text" name="name_fa" required value={formData.name_fa} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">نام انگلیسی *</label>
                    <input type="text" name="name_en" required value={formData.name_en} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">پایتخت *</label>
                    <input type="text" name="capital_fa" required value={formData.capital_fa} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">تصویر کشور</label>
                  {formData.image_url && <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover rounded-lg mb-2" />}
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer transition-colors">
                    <Upload size={20} />
                    {uploading ? 'در حال آپلود...' : 'آپلود تصویر'}
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'image_url')} className="hidden" disabled={uploading} />
                  </label>
                </div>

                {/* Major Cities */}
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">شهرهای مهم (برای تور مجازی)</label>
                  <div className="flex gap-2 mb-2">
                    <input type="text" value={cityInput.name} onChange={(e) => setCityInput({ ...cityInput, name: e.target.value })} placeholder="نام شهر"
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <label className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer transition-colors">
                      عکس
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'city_image')} className="hidden" />
                    </label>
                    <button type="button" onClick={addCity} className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB]">افزودن</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.major_cities.map((city, idx) => (
                      <div key={`city-${city.name}-${idx}`} className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg text-sm">
                        <span>{city.name}</span>
                        <button type="button" onClick={() => removeCity(idx)} className="text-red-400 hover:text-red-300"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scores */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">امنیت (0-10)</label>
                    <input type="number" name="security_score" step="0.1" min="0" max="10" value={formData.security_score} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">کیفیت زندگی (0-10)</label>
                    <input type="number" name="quality_of_life_score" step="0.1" min="0" max="10" value={formData.quality_of_life_score} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">آموزش (0-10)</label>
                    <input type="number" name="education_score" step="0.1" min="0" max="10" value={formData.education_score} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">بهداشت (0-10)</label>
                    <input type="number" name="healthcare_score" step="0.1" min="0" max="10" value={formData.healthcare_score} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                </div>

                {/* Lists (comma-separated) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">مزایا (با کاما)</label>
                    <textarea name="pros" rows="2" value={formData.pros} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"></textarea>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">معایب (با کاما)</label>
                    <textarea name="cons" rows="2" value={formData.cons} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none resize-none"></textarea>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button type="submit" className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors">
                    {editingCountry ? 'ذخیره تغییرات' : 'ایجاد کشور'}
                  </button>
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors">
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCountries;